import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  FileText,
  Pen,
  RefreshCw,
  Users,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import {
  getAppointmentsByDateRange,
  getQueueMy,
  queueCompleteConsultation,
  type QueueEntry,
  type QueueMyResponse,
  queueStartConsultation,
  updateAppointment,
} from '@/lib/api';
import { format } from 'date-fns';
import { DashboardIcon } from '@radix-ui/react-icons';
import PatientProfileModal from '@/components/Patient/PatientProfileModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import { useAuth } from '@/context/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Appointment {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: 'scheduled' | 'checked_in' | 'completed' | 'cancelled' | 'no_show' | 'missed' | 'in_progress';
  visit_type?: 'consultation' | 'follow_up' | 'check_up' | 'emergency';
  notes?: string;
  created_at: string;
  updated_at: string;
  // Inline patient fields from API
  patient_name?: string;
  patient_age?: number;
  patient_gender?: string;
  patient_phone?: string;
}

interface AppointmentWithPatientData extends Appointment {
  patientName: string;
  patientAge: number;
  patientGender: string;
  patientPhone: string;
  appointmentTime: Date;
  isToday: boolean;
  isPast: boolean;
  isUpcoming: boolean;
}

interface DashboardStats {
  patientsSeenToday: number;
  totalAppointments: number;
  pendingAppointments: number;
  cancelledAppointments: number;
}

// ─── Pure helpers (no component state deps) ───────────────────────────────────

function getAppointmentTiming(apt: Appointment) {
  try {
    const now = new Date();
    const dateOnly = apt.date.split('T')[0];
    const aptDateTime = new Date(`${dateOnly} ${apt.time}`);
    const isToday = aptDateTime.toDateString() === now.toDateString();
    const timeDiff = aptDateTime.getTime() - now.getTime();
    return {
      isToday,
      isPast: timeDiff < 0 && isToday,
      isUpcoming: timeDiff > 0 && isToday,
      appointmentTime: aptDateTime,
    };
  } catch {
    return { isToday: false, isPast: false, isUpcoming: false, appointmentTime: new Date() };
  }
}

function formatTime(timeString: string) {
  if (!timeString) return 'Not specified';
  try {
    const [h, m] = timeString.split(':').map(Number);
    const d = new Date(); d.setHours(h, m, 0, 0);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return timeString;
  }
}

const VISIT_TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation', follow_up: 'Follow-up',
  check_up: 'Check-up', emergency: 'Emergency',
};
const VISIT_TYPE_COLORS: Record<string, string> = {
  consultation: 'text-primary bg-primary/10', follow_up: 'text-warning bg-warning/10',
  check_up: 'text-success bg-success/10', emergency: 'text-destructive bg-destructive/10',
};

function getVisitTypeLabel(t?: string) { return VISIT_TYPE_LABELS[t ?? ''] || 'General Visit'; }
function getVisitTypeColor(t?: string) { return VISIT_TYPE_COLORS[t ?? ''] || 'text-muted-foreground bg-muted/10'; }

function getInitials(name: string) {
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function calcWaitingTime(entry: QueueEntry) {
  if (entry.waitingTimeMinutes == null) return 'Waiting';
  if (entry.waitingTimeMinutes < 5) return 'Just arrived';
  if (entry.waitingTimeMinutes < 60) return `Waiting · ${entry.waitingTimeMinutes} mins`;
  const h = Math.floor(entry.waitingTimeMinutes / 60);
  const m = entry.waitingTimeMinutes % 60;
  return `Waiting · ${h}h ${m}m`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Home() {
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { currentUser } = useAuth();

  useEffect(() => { document.title = 'Dashboard | OneCheckUp'; }, []);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showPatientProfileModal, setShowPatientProfileModal] = useState<AppointmentWithPatientData | null>(null);
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [myQueue, setMyQueue] = useState<QueueMyResponse | null>(null);
  const [queueActionId, setQueueActionId] = useState<string | null>(null);

  // Show doctor dashboard if user has clinical profile (doctor_id), regardless of role
  const isDoctor = !!currentUser?.doctor_id;

  // ── Data loading ─────────────────────────────────────────────────────────────

  const loadDashboardData = useCallback(async () => {
    try {
      if (!currentUser?.clinic_id) {
        setAppointments([]);
        setMyQueue(null);
        return;
      }
      const today = format(new Date(), 'yyyy-MM-dd');
      const doctorFilter = currentUser.doctor_id ?? undefined;
      const [appointmentsData, queueData] = await Promise.all([
        getAppointmentsByDateRange(today, today, currentUser.clinic_id, doctorFilter),
        currentUser.doctor_id ? getQueueMy() : Promise.resolve(null),
      ]);
      if (appointmentsData) setAppointments(appointmentsData);
      setMyQueue(queueData ?? null);
    } catch (err) {
      handleError(err, 'Dashboard Loading');
    } finally {
      setInitialized(true);
    }
  }, [currentUser?.clinic_id, currentUser?.role, currentUser?.doctor_id]);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  // Poll every 60 s; skip when tab is hidden so background tabs don't waste calls
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadDashboardData();
    }, 60_000);
    return () => clearInterval(id);
  }, [loadDashboardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  // ── Parsing — uses inline patient data from API ────────────────────────────

  const parseAppointmentData = useCallback((apt: Appointment): AppointmentWithPatientData => {
    const timing = getAppointmentTiming(apt);
    return {
      ...apt,
      patientName:   apt.patient_name   || 'Unknown',
      patientAge:    apt.patient_age    || 0,
      patientGender: apt.patient_gender || '',
      patientPhone:  apt.patient_phone  || '',
      ...timing,
    };
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────

  const todayAppointmentsWithStatus = useMemo<AppointmentWithPatientData[]>(() =>
    appointments
      .map(parseAppointmentData)
      .sort((a, b) => a.appointmentTime.getTime() - b.appointmentTime.getTime()),
  [appointments, parseAppointmentData]);

  const stats: DashboardStats = useMemo(() => ({
    patientsSeenToday:    todayAppointmentsWithStatus.filter(a => a.status === 'completed').length,
    totalAppointments:    todayAppointmentsWithStatus.length,
    pendingAppointments:  todayAppointmentsWithStatus.filter(a => a.status === 'scheduled').length,
    cancelledAppointments: todayAppointmentsWithStatus.filter(a => a.status === 'cancelled' || a.status === 'no_show').length,
  }), [todayAppointmentsWithStatus]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const updateAppointmentStatus = useCallback(async (apt: AppointmentWithPatientData, newStatus: Appointment['status']) => {
    setAppointments(prev =>
      prev.map(a => a.id === apt.id ? { ...a, status: newStatus, updated_at: new Date().toISOString() } : a)
    );
    try {
      await updateAppointment({ id: apt.id, status: newStatus, updated_at: new Date().toISOString() });
    } catch {
      await loadDashboardData();
    }
  }, [loadDashboardData]);

  const handleAppointmentAction = useCallback(async (
    apt: AppointmentWithPatientData,
    action: 'start' | 'profile' | 'progress'
  ) => {
    if (!apt?.id) return;
    try {
      switch (action) {
        case 'start':
          await updateAppointmentStatus(apt, 'in_progress');
          navigate(`/patients/${apt.patient_id}?openPrescription=true`);
          break;
        case 'profile':
          setShowPatientProfileModal(apt);
          break;
        case 'progress':
          await updateAppointmentStatus(apt, 'completed');
          toast({ title: 'Consultation completed', description: apt.patientName });
          break;
      }
    } catch {
      toast({ title: 'Action failed', description: 'Unable to complete action.', variant: 'destructive' });
    }
  }, [updateAppointmentStatus, navigate]);

  const handleQueueStart = useCallback(async (entry: QueueEntry) => {
    if (!entry.patientId) {
      toast({ title: 'Error', description: 'No patient linked to this queue entry.', variant: 'destructive' });
      return;
    }
    try {
      setQueueActionId(entry.id);
      await queueStartConsultation(entry.id);
      toast({ title: 'Consultation started', description: `Calling ${entry.patientName} (Token #${entry.tokenNumber})` });
      await loadDashboardData();
      navigate(`/patients/${entry.patientId}?openPrescription=true`);
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  }, [loadDashboardData, navigate]);

  const handleQueueComplete = useCallback(async (entry: QueueEntry) => {
    try {
      setQueueActionId(entry.id);
      await queueCompleteConsultation(entry.id);
      toast({ title: 'Consultation completed', description: `${entry.patientName} — Token #${entry.tokenNumber}` });
      await loadDashboardData();
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    } finally {
      setQueueActionId(null);
    }
  }, [loadDashboardData]);

  const handleKPICardClick = () => {
    navigate('/appointments');
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'write-prescription': navigate('/prescriptions', { state: { mode: 'create' } }); break;
      case 'add-appointment':    setShowAddAppointmentModal(true); break;
    }
  };

  // ── Loading state ──────────────────────────────────────────────────────────

  if (!initialized) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const inConsultation = myQueue?.entries.find(e => e.status === 'in_consultation');
  const waitingEntries = myQueue?.entries.filter(e => e.status === 'waiting') ?? [];
  const nextUp = waitingEntries[0];
  const remainingWaiting = waitingEntries.slice(1);

  return (
    <div className="prescription-form-container">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary rounded-lg">
            <DashboardIcon className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h3 className="text-base font-bold text-foreground">Dashboard</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Quick actions */}
          <div className="flex items-center gap-1 border border-border rounded-lg p-1">
            <button onClick={handleRefresh} disabled={refreshing} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors" title="Refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={() => handleQuickAction('add-appointment')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors" title="Add Appointment">
              <Calendar className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Appointment</span>
            </button>
            {isDoctor && (
              <button onClick={() => handleQuickAction('write-prescription')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors" title="Write Prescription">
                <Pen className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Prescription</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Staff: FrontDesk shortcut */}
      {!isDoctor && (
        <div
          onClick={() => navigate('/frontdesk')}
          className="flex items-center justify-between p-4 mb-6 rounded-xl border border-primary/20 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm">Front Desk</p>
              <p className="text-xs text-muted-foreground">Queue management, walk-ins, check-ins</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-primary text-sm font-medium">
            Open <span className="text-base">→</span>
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">

        {/* Doctor queue panel */}
        {isDoctor && myQueue && (
          <div className="lg:col-span-3">
            <Card className="medical-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-warning" /> My Queue
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    {myQueue.summary.totalWaiting} waiting{myQueue.summary.totalInConsultation > 0 ? ` · ${myQueue.summary.totalInConsultation} in consultation` : ''}
                  </span>
                </div>

                {!inConsultation && waitingEntries.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Users className="w-10 h-10 text-muted-foreground/30 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">Queue is empty</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Front Desk will add patients here</p>
                  </div>
                ) : (
                  <>
                    {/* In Consultation */}
                    {inConsultation && (
                      <div className="ring-2 ring-primary/30 bg-primary/5 rounded-xl p-4 mb-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Now In Consultation</p>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 rounded-full bg-warning/20 flex items-center justify-center">
                            <span className="text-sm font-bold text-warning">{inConsultation.tokenNumber}</span>
                          </div>
                          <div>
                            <p className="font-bold text-lg text-foreground">{inConsultation.patientName}</p>
                            <p className="text-xs text-muted-foreground">In Consultation</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/patients/${inConsultation.patientId}?openPrescription=true`)}
                            className="flex-1 medical-button-secondary text-sm"
                          >
                            Open Prescription
                          </button>
                          <button
                            onClick={() => handleQueueComplete(inConsultation)}
                            disabled={queueActionId !== null}
                            className="flex-1 text-sm py-2 px-4 font-medium rounded-md bg-success text-success-foreground hover:bg-success/90 disabled:opacity-50 transition-colors"
                          >
                            {queueActionId === inConsultation.id ? 'Completing…' : 'Complete →'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Next Up */}
                    {nextUp ? (
                      <div className="bg-muted/30 rounded-xl p-4 mb-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Next Up</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-warning/20 flex items-center justify-center">
                              <span className="text-sm font-bold text-warning">{nextUp.tokenNumber}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">{nextUp.patientName}</p>
                              <p className="text-xs text-muted-foreground">{calcWaitingTime(nextUp)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleQueueStart(nextUp)}
                            disabled={queueActionId !== null}
                            className="medical-button-primary text-sm"
                          >
                            {queueActionId === nextUp.id ? 'Starting…' : 'Call Next'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-muted/10 rounded-xl p-4 mb-4 flex items-center justify-center min-h-[80px]">
                        <p className="text-sm text-muted-foreground">No patients waiting</p>
                      </div>
                    )}

                    {/* Waiting list */}
                    {remainingWaiting.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Waiting</p>
                        <div className="space-y-2">
                          {remainingWaiting.map(entry => (
                            <div key={entry.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/20">
                              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                                <span className="text-xs font-bold text-muted-foreground">{entry.tokenNumber}</span>
                              </div>
                              <p className="text-sm text-foreground flex-1">{entry.patientName}</p>
                              {entry.waitingTimeMinutes != null && (
                                <span className="text-xs text-muted-foreground">{calcWaitingTime(entry)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Today's Appointments */}
        <div className={isDoctor && myQueue ? 'lg:col-span-2' : 'lg:col-span-5'}>
          <Card className="medical-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Today's Appointments ({todayAppointmentsWithStatus.length})
                </h4>
                <button onClick={() => navigate('/appointments')} className="medical-button-secondary text-sm">
                  View All
                </button>
              </div>

              {todayAppointmentsWithStatus.length > 0 ? (
                <div className="space-y-3">
                  {todayAppointmentsWithStatus.map(apt => {
                  const isImminent = apt.isUpcoming && apt.appointmentTime.getTime() - Date.now() <= 1_800_000;
                  return (
                    <div
                      key={apt.id}
                      className={`medical-section p-3 transition-all hover:shadow-md ${
                        isImminent && apt.status === 'scheduled' ? 'ring-2 ring-warning/20 bg-warning/5' : ''
                      } ${apt.status === 'completed' ? 'bg-success/5 border-success/20' : ''
                      } ${apt.isPast && apt.status === 'scheduled' ? 'bg-destructive/5 border-destructive/20' : ''
                      } ${apt.status === 'in_progress' ? 'ring-2 ring-primary/30 bg-primary/5' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="text-center min-w-[70px]">
                          <div className="text-sm font-bold text-foreground">{formatTime(apt.time)}</div>
                        </div>
                        <div className="flex items-center justify-between w-full gap-2">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                              apt.status === 'completed' ? 'bg-success/10 text-success' :
                              apt.status === 'in_progress' ? 'bg-primary/10 text-primary' :
                              isImminent && apt.status === 'scheduled' ? 'bg-warning/10 text-warning' :
                              'bg-primary/10 text-primary'
                            }`}>
                              {getInitials(apt.patientName)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-foreground truncate">{apt.patientName}</h4>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span>
                                  {[
                                    apt.patientAge ? `${apt.patientAge}y` : null,
                                    apt.patientGender || null,
                                    apt.reason ? `${apt.reason.substring(0, 30)}${apt.reason.length > 30 ? '…' : ''}` : null,
                                  ].filter(Boolean).join(' · ')}
                                </span>
                                {apt.visit_type && (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getVisitTypeColor(apt.visit_type)}`}>
                                    {getVisitTypeLabel(apt.visit_type)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                              apt.status === 'completed' ? 'bg-success/10 text-success' :
                              apt.status === 'cancelled' ? 'bg-destructive/10 text-destructive' :
                              apt.status === 'in_progress' ? 'bg-primary/10 text-primary' :
                              apt.isPast && apt.status === 'scheduled' ? 'bg-destructive/10 text-destructive' :
                              isImminent && apt.status === 'scheduled' ? 'bg-warning/10 text-warning' :
                              'bg-primary/10 text-primary'
                            }`}>
                              {apt.status === 'in_progress' ? 'In Progress' :
                               apt.isPast && apt.status === 'scheduled' ? 'Missed' :
                               apt.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </span>

                            {apt.status === 'scheduled' ? (
                              <button onClick={() => handleAppointmentAction(apt, 'start')} className="medical-button-primary text-sm whitespace-nowrap">
                                Start
                              </button>
                            ) : apt.status === 'in_progress' ? (
                              <button onClick={() => handleAppointmentAction(apt, 'progress')} className="px-3 py-1.5 text-sm font-medium rounded-md bg-success text-success-foreground hover:bg-success/90 whitespace-nowrap transition-colors">
                                Complete
                              </button>
                            ) : null}

                            {apt.status !== 'cancelled' && (
                              <button onClick={() => handleAppointmentAction(apt, 'profile')} className="p-2 text-muted-foreground hover:text-info hover:bg-info/10 rounded-lg transition-colors" title="View Patient Profile">
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-success mx-auto mb-3" />
                  <p className="text-muted-foreground mb-2">No appointments today</p>
                  <p className="text-sm text-muted-foreground">Schedule appointments to see them here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Card className="medical-card stats-card cursor-pointer hover:shadow-lg transition-all duration-200" onClick={handleKPICardClick}>
          <CardContent className="p-2.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Patients Seen Today</p>
                <p className="text-xl font-bold text-success">{stats.patientsSeenToday}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.totalAppointments > 0 ? Math.round((stats.patientsSeenToday / stats.totalAppointments) * 100) : 0}% completion rate
                </p>
              </div>
              <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="medical-card stats-card cursor-pointer hover:shadow-lg transition-all duration-200" onClick={handleKPICardClick}>
          <CardContent className="p-2.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Pending Appointments</p>
                <p className="text-xl font-bold text-primary">{stats.pendingAppointments}</p>
                <p className="text-xs text-muted-foreground mt-1">of {stats.totalAppointments} scheduled today</p>
              </div>
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="medical-card stats-card cursor-pointer hover:shadow-lg transition-all duration-200" onClick={handleKPICardClick}>
          <CardContent className="p-2.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Total Appointments</p>
                <p className="text-xl font-bold text-warning">{stats.totalAppointments}</p>
                <p className="text-xs text-muted-foreground mt-1">{stats.pendingAppointments} pending today</p>
              </div>
              <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-warning" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="medical-card stats-card cursor-pointer hover:shadow-lg transition-all duration-200" onClick={handleKPICardClick}>
          <CardContent className="p-2.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Cancelled / No-show</p>
                <p className="text-xl font-bold text-destructive">{stats.cancelledAppointments}</p>
                <p className="text-xs text-muted-foreground mt-1">today</p>
              </div>
              <div className="w-8 h-8 bg-destructive/10 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <PatientProfileModal
        patient={showPatientProfileModal}
        isOpen={!!showPatientProfileModal}
        onClose={() => setShowPatientProfileModal(null)}
        onStartConsultation={apt => handleAppointmentAction(apt as AppointmentWithPatientData, 'start')}
      />

      <AddAppointmentModal
        isOpen={showAddAppointmentModal}
        onClose={() => setShowAddAppointmentModal(false)}
        onAdded={() => {
          loadDashboardData();
          setShowAddAppointmentModal(false);
          toast({ title: 'Appointment Created', description: 'New appointment scheduled successfully.' });
        }}
        patients={[]}
        showPatientSelection
        title="New Appointment"
        description="Schedule a new patient appointment"
      />
    </div>
  );
}
